import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { pensionFundsApi } from '../../services/api';
import { ArrowLeft, Clock, User, Building2, CreditCard, FileText, Loader2, ExternalLink } from 'lucide-react';
import type { PensionFund } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import PensionFundDocuments from './PensionFundDocuments';
import PensionFundStatusForm from './PensionFundStatusForm';

const PensionFundDetails: React.FC = () => {
  const notify = useNotification();
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'actions' | 'informations' | 'documents' | 'activite'>('actions');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showInstructionFields, setShowInstructionFields] = useState(false);
  const [showAmountField, setShowAmountField] = useState(false);

  const instructionLemaniaRef = useRef<HTMLInputElement>(null);
  const instructionBanqueRef = useRef<HTMLInputElement>(null);
  const decompteRef = useRef<HTMLInputElement>(null);

  // PDF Merge functionality for Nouveau -> En cours transition (Institution supplétive only)
  const [mergeDocsSelected, setMergeDocsSelected] = useState<{
    procuration: boolean;
    instruction_institution: boolean;
    photo: boolean;
    photo2: boolean;
  }>({
    procuration: false,
    instruction_institution: false,
    photo: false,
    photo2: false,
  });
  const [isMerging, setIsMerging] = useState(false);

  // AEIS Uebertrag submission state (for Institution supplétive)
  const [uebertragResult, setUebertragResult] = useState<{ success: boolean; message: string; screenshot?: string | null; screenshots?: Record<string, string> | null; demande_transfert?: string } | null>(null);
  const [uebertragIsSubmitting, setUebertragIsSubmitting] = useState(false);

  const { data: pensionFund, isLoading } = useQuery({
    queryKey: ['pension-fund', uuid],
    queryFn: async () => {
      const response = await pensionFundsApi.getDetails(uuid!);
      return response.data.data as PensionFund;
    },
    enabled: !!uuid,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (data: {
      status: string;
      reason?: string;
      amount?: number;
      exit_statement_date?: string;
      commission_amount?: number;
      commissioned?: boolean;
      regroupement_fondation?: string;
      reference?: string;
      files?: { instruction_lemania?: File; instruction_banque?: File }
    }) => {
      // Send all data to updateStatus endpoint
      await pensionFundsApi.updateStatus(uuid!, data.status, data.reason, {
        amount: data.amount,
        exit_statement_date: data.exit_statement_date,
        commission_amount: data.commission_amount,
        commissioned: data.commissioned,
        regroupement_fondation: data.regroupement_fondation,
        reference: data.reference,
      });

      if (data.files && (data.files.instruction_lemania || data.files.instruction_banque)) {
        await pensionFundsApi.uploadInstruction(uuid!, data.files);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pension-fund', uuid] });
      setSelectedStatus('');
      setShowInstructionFields(false);
      setShowAmountField(false);
      notify.success('Statut mis à jour avec succès');
    },
  });

  const uploadDecompteMutation = useMutation({
    mutationFn: (file: File) => pensionFundsApi.uploadDecompte(uuid!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pension-fund', uuid] });
      if (decompteRef.current) decompteRef.current.value = '';
      notify.success('Décompte téléversé avec succès');
    },
  });

  const uploadInstructionMutation = useMutation({
    mutationFn: (files: { instruction_lemania?: File; instruction_banque?: File }) =>
      pensionFundsApi.uploadInstruction(uuid!, files),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pension-fund', uuid] });
      notify.success('Instructions téléversées avec succès');
    },
  });

  const uploadDemandeTransfertMutation = useMutation({
    mutationFn: (file: File) => pensionFundsApi.uploadDemandeTransfert(uuid!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pension-fund', uuid] });
      notify.success('Demande de transfert téléversée avec succès');
    },
  });

  const generateDemandeTransfertMutation = useMutation({
    mutationFn: () => pensionFundsApi.generateDemandeTransfert(uuid!),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['pension-fund', uuid] });
      notify.success(response.data.message || 'Demande de transfert générée avec succès');
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (documentType: string) => pensionFundsApi.deleteDocument(uuid!, documentType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pension-fund', uuid] });
      notify.success('Document supprimé avec succès');
    },
    onError: () => {
      notify.error('Erreur lors de la suppression du document');
    },
  });

  const validateMutation = useMutation({
    mutationFn: () => pensionFundsApi.validateTransfer(uuid!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pension-fund', uuid] });
      notify.success('Transfert validé avec succès');
    },
  });

  const signInstructionMutation = useMutation({
    mutationFn: (fondationName: string) => pensionFundsApi.signInstruction(uuid!, fondationName),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['pension-fund', uuid] });
      notify.success(response.data.message || 'Instruction signée avec succès');
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { message?: string } } }).response?.data?.message || 'Erreur lors de la signature de l\'instruction';
      notify.error(message);
    },
  });

  // Handle PDF merge for Nouveau -> En cours transition
  const handleMergeDocuments = async () => {
    if (!pensionFund?.customer) {
      notify.error('Données client non disponibles');
      return;
    }

    const customer = pensionFund.customer;
    const selectedDocs: { path: string; name: string }[] = [];

    if (mergeDocsSelected.procuration && customer.procuration) {
      selectedDocs.push({ path: customer.procuration, name: 'procuration' });
    }
    if (mergeDocsSelected.instruction_institution && customer.instruction) {
      selectedDocs.push({ path: customer.instruction, name: 'instruction_institution' });
    }
    if (mergeDocsSelected.photo && customer.photo) {
      selectedDocs.push({ path: customer.photo, name: 'photo' });
    }
    if (mergeDocsSelected.photo2 && customer.photo2) {
      selectedDocs.push({ path: customer.photo2, name: 'photo2' });
    }

    if (selectedDocs.length < 2) {
      notify.error('Veuillez sélectionner au moins 2 documents à fusionner');
      return;
    }

    setIsMerging(true);

    try {
      // Fetch each document and create File objects
      const files: File[] = [];

      for (const doc of selectedDocs) {
        const response = await api.post('/tools/fetch-document', { path: doc.path }, {
          responseType: 'blob',
        });
        // Preserve original extension from path
        const originalExt = doc.path.split('.').pop() || 'pdf';
        const contentType = response.headers['content-type'] || 'application/octet-stream';
        const file = new File([response.data], `${doc.name}.${originalExt}`, { type: contentType });
        files.push(file);
      }

      // Merge the PDFs
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files[]', file);
      });

      const mergeResponse = await api.post('/tools/merge-pdf', formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Download the merged file
      const url = window.URL.createObjectURL(new Blob([mergeResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${pensionFund.client_name || 'documents'}_${pensionFund.fund_name}_merged.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Reset selection
      setMergeDocsSelected({ procuration: false, instruction_institution: false, photo: false, photo2: false });
    } catch (error: unknown) {
      console.error('Merge error:', error);
      const errObj = error as { response?: { data?: { message?: string } }; message?: string };
      notify.error(`Erreur lors de la fusion: ${errObj.response?.data?.message || errObj.message || 'Erreur inconnue'}`);
    } finally {
      setIsMerging(false);
    }
  };

  // Get available status options based on current status (workflow)
  const getAvailableStatuses = (currentStatus: string): string[] => {
    switch (currentStatus) {
      case 'Nouveau':
        return ['En cours', 'Annulé'];
      case 'En cours':
        return ['Annulé', 'Rejeté', 'Transféré'];
      case 'Rejeté':
        return ['En cours'];
      case 'Transféré':
        return ['Terminé'];
      default:
        // For other statuses, user will provide the workflow rules
        return ['Nouveau', 'En cours', 'Rejeté', 'Annulé', 'Transféré', 'Terminé'];
    }
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    // Show instruction fields when changing TO "En cours"
    if (status === 'En cours') {
      setShowInstructionFields(true);
      setShowAmountField(false);
    }
    // Show amount and exit statement fields when changing FROM "En cours" TO "Transféré"
    else if (status === 'Transféré') {
      setShowInstructionFields(false);
      setShowAmountField(true);
    }
    // Show commission amount when changing FROM "Transféré" TO "Terminé"
    else if (status === 'Terminé') {
      setShowInstructionFields(false);
      setShowAmountField(true);
    } else {
      setShowInstructionFields(false);
      setShowAmountField(false);
    }
  };

  const handleConfirmStatus = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);

    // Validation for "En cours" status - requires instruction documents
    if (selectedStatus === 'En cours') {
      const hasLemaniaInstruction = pensionFund?.lemania_instruction_document || instructionLemaniaRef.current?.files?.[0];
      const hasBanqueInstruction = pensionFund?.instruction_document || instructionBanqueRef.current?.files?.[0];

      if (!hasLemaniaInstruction || !hasBanqueInstruction) {
        notify.error('Pour passer à "En cours", vous devez uploader: Instruction institution et Instruction Banque');
        return;
      }
    }

    // Validation for En cours -> Transféré transition
    if (pensionFund?.status === 'En cours' && selectedStatus === 'Transféré') {
      const amount = formData.get('amount') as string;
      const exitStatementDate = formData.get('exit_statement_date') as string;
      const hasExitStatement = pensionFund.exit_statement_document || decompteRef.current?.files?.[0];

      if (!amount || !exitStatementDate || !hasExitStatement) {
        notify.error('Pour passer à "Transféré", vous devez renseigner: le montant, la date du décompte de sortie et le document décompte de sortie');
        return;
      }
    }

    // Validation for Transféré -> Terminé transition
    if (pensionFund?.status === 'Transféré' && selectedStatus === 'Terminé') {
      const commissionAmount = formData.get('commission_amount') as string;
      if (!commissionAmount) {
        notify.error('Pour passer à "Terminé", vous devez renseigner le montant de la commission');
        return;
      }
    }

    // Special handling for Transféré status - need to upload décompte first
    if (selectedStatus === 'Transféré') {
      const decompteFile = decompteRef.current?.files?.[0];
      const amount = formData.get('amount') as string;
      const exitStatementDate = formData.get('exit_statement_date') as string;

      // If there's a new décompte file, upload it first
      if (decompteFile) {
        const uploadPromise = pensionFundsApi.uploadDecompte(uuid!, decompteFile);
        uploadPromise.then(() => {
          // After upload, update status with amount and date
          updateStatusMutation.mutate({
            status: selectedStatus,
            amount: parseFloat(amount),
            exit_statement_date: exitStatementDate,
            reason: formData.get('reason') as string || undefined,
          });
        }).catch((error) => {
          notify.error('Erreur lors de l\'upload du décompte: ' + error.message);
        });
        return;
      } else {
        // No new file, just update with amount and date
        updateStatusMutation.mutate({
          status: selectedStatus,
          amount: parseFloat(amount),
          exit_statement_date: exitStatementDate,
          reason: formData.get('reason') as string || undefined,
        });
        return;
      }
    }

    const data: {
      status: string;
      reason?: string;
      amount?: number;
      commission_amount?: number;
      commissioned?: boolean;
      regroupement_fondation?: string;
      reference?: string;
      files?: { instruction_lemania?: File; instruction_banque?: File };
    } = {
      status: selectedStatus,
      reason: formData.get('reason') as string || undefined,
    };

    // Include regroupement fondation if selected
    const regroupementFondation = formData.get('regroupement_fondation') as string;
    if (regroupementFondation) {
      data.regroupement_fondation = regroupementFondation;
    }

    // Include reference if provided
    const reference = formData.get('reference') as string;
    if (reference) {
      data.reference = reference;
    }

    // Handle commission (for Terminé status) - automatically set commissioned to yes
    if (selectedStatus === 'Terminé') {
      data.commission_amount = parseFloat(formData.get('commission_amount') as string);
      data.commissioned = true;
    }

    if (showInstructionFields) {
      const instruction_lemania = instructionLemaniaRef.current?.files?.[0];
      const instruction_banque = instructionBanqueRef.current?.files?.[0];
      if (instruction_lemania || instruction_banque) {
        data.files = { instruction_lemania, instruction_banque };
      }
    }

    updateStatusMutation.mutate(data);
  };

  const handleFileUpload = (field: string, file: File) => {
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      notify.error('Le fichier est trop volumineux. Taille maximale: 10MB');
      return;
    }

    const allowedTypes = ['application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      notify.error('Type de fichier non autorisé. Formats acceptés: PDF');
      return;
    }

    // Map frontend field names to backend upload handlers
    switch (field) {
      case 'exit_statement':
        uploadDecompteMutation.mutate(file);
        break;
      case 'demande_transfert':
        uploadDemandeTransfertMutation.mutate(file);
        break;
      case 'lemania_instruction':
        uploadInstructionMutation.mutate({ instruction_lemania: file });
        break;
      case 'instruction':
        uploadInstructionMutation.mutate({ instruction_banque: file });
        break;
      default:
        console.error('Unknown field type:', field);
        notify.error('Type de document non reconnu');
    }
  };

  const handleFileDelete = (field: string, documentName: string) => {
    const confirmDelete = window.confirm(
      `Êtes-vous sûr de vouloir supprimer ${documentName}?`
    );

    if (confirmDelete) {
      deleteDocumentMutation.mutate(field);
    }
  };

  const handleUebertragSubmit = async (fondation?: string, reference?: string) => {
    if (!uuid) return;
    setUebertragIsSubmitting(true);
    setUebertragResult(null);
    try {
      const response = await pensionFundsApi.submitUebertrag(uuid, fondation, reference);
      setUebertragResult(response.data);
    } catch (error: unknown) {
      const errObj = error as { response?: { data?: { success: boolean; message: string; screenshot?: string | null; screenshots?: Record<string, string> | null } }; message?: string };
      setUebertragResult(errObj.response?.data || { success: false, message: errObj.message || 'Erreur inconnue' });
    } finally {
      setUebertragIsSubmitting(false);
    }
  };

  const getFileUrl = (path: string | null | undefined): string => {
    if (!path) return '';
    // If it's already a full URL, return as-is
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }

    // Files stored in laravel-api storage (caisses, generated instructions)
    const backofficeStoragePrefixes = ['caisses/', 'instructions/'];
    const isBackofficeFile = backofficeStoragePrefixes.some(prefix => path.startsWith(prefix));

    if (isBackofficeFile) {
      // Use laravel-api files endpoint (derive from API URL)
      const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8001/api/v1';
      const baseUrl = apiUrl.replace('/api/v1', '');
      return `${baseUrl}/api/files?path=${encodeURIComponent(path)}`;
    }

    // Use lpp-api files endpoint for other files (customer documents)
    const filesApiUrl = process.env.REACT_APP_FILES_API_URL || 'http://127.0.0.1:8010/api/files';
    return `${filesApiUrl}?path=${encodeURIComponent(path)}`;
  };

  // Get status color for display
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Nouveau': return { bg: 'from-gray-500 to-gray-600', light: 'bg-gray-100 text-gray-800' };
      case 'En cours': return { bg: 'from-blue-500 to-indigo-600', light: 'bg-blue-100 text-blue-800' };
      case 'Rejeté': return { bg: 'from-red-500 to-red-600', light: 'bg-red-100 text-red-800' };
      case 'Annulé': return { bg: 'from-orange-500 to-orange-600', light: 'bg-orange-100 text-orange-800' };
      case 'Transféré': return { bg: 'from-green-500 to-emerald-600', light: 'bg-green-100 text-green-800' };
      case 'Terminé': return { bg: 'from-green-600 to-green-700', light: 'bg-green-100 text-green-800' };
      default: return { bg: 'from-gray-500 to-gray-600', light: 'bg-gray-100 text-gray-800' };
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!pensionFund) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-2">Caisse de pension non trouvée</p>
      </div>
    );
  }

  const statusColors = getStatusColor(pensionFund.status);

  return (
    <div className="min-h-screen bg-gray-50 -m-4 lg:-m-6">
      {/* Header with Back Button */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/pension-funds')}
            className="text-gray-600 hover:text-gray-900 flex items-center"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour
          </button>
        </div>
      </div>

      {/* Pension Fund Recap Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="flex items-center">
              <Building2 className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <p className="text-xs text-gray-500">Caisse</p>
                <p className="text-sm font-medium text-gray-900">{pensionFund.fund_name}</p>
              </div>
            </div>
            <div className="flex items-center">
              <User className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <p className="text-xs text-gray-500">Client</p>
                <p className="text-sm font-medium text-gray-900">{pensionFund.client_name}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <p className="text-xs text-gray-500">Fondation</p>
                <p className="text-sm font-medium text-gray-900">{pensionFund.fondation || 'Non renseigné'}</p>
              </div>
            </div>
            <div className="flex items-center">
              <CreditCard className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <p className="text-xs text-gray-500">Montant</p>
                <p className="text-sm font-medium text-gray-900">
                  {pensionFund.amount ? `CHF ${parseFloat(pensionFund.amount).toLocaleString('fr-CH')}` : '-'}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors.light}`}>
                {pensionFund.status || 'Non défini'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'actions', label: 'Actions' },
              { key: 'informations', label: 'Informations' },
              { key: 'documents', label: 'Documents' },
              { key: 'activite', label: 'Activité' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as 'actions' | 'informations' | 'documents' | 'activite')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

      {activeTab === 'informations' && (
        <div className="space-y-6">
          {/* Customer Information Card */}
          {pensionFund.customer && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-gray-900">
                  Informations client
                </h2>
                <button
                  onClick={() => navigate(`/customers/${pensionFund.customer_uuid}`)}
                  className="flex items-center px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Voir le dossier client
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet
                  </label>
                  <div className="flex items-center px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                    <User className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-900">{pensionFund.client_name || '-'}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AVS
                  </label>
                  <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                    {pensionFund.customer?.avs || '-'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date de naissance
                  </label>
                  <div className="flex items-center px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                    <Clock className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-gray-900">{pensionFund.customer?.dat || '-'}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fondation du client
                  </label>
                  <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                    {pensionFund.customer?.fondation || '-'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Adresse
                  </label>
                  <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                    {pensionFund.customer?.adr || '-'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    NPA / Localité
                  </label>
                  <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                    {pensionFund.customer?.npa && pensionFund.customer?.loc
                      ? `${pensionFund.customer.npa} ${pensionFund.customer.loc}`
                      : '-'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                    {pensionFund.customer?.tel || '-'}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    État du dossier
                  </label>
                  <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      pensionFund.customer?.state === 1 ? 'bg-blue-100 text-blue-800' :
                      pensionFund.customer?.state === 2 ? 'bg-emerald-100 text-emerald-800' :
                      pensionFund.customer?.state === 3 ? 'bg-amber-100 text-amber-800' :
                      pensionFund.customer?.state === 4 ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {pensionFund.customer?.state === 1 && 'Contrôle administratif'}
                      {pensionFund.customer?.state === 2 && 'Soumission institutions'}
                      {pensionFund.customer?.state === 3 && 'Attente réponses'}
                      {pensionFund.customer?.state === 4 && 'Dossier clôturé'}
                      {!pensionFund.customer?.state && 'État inconnu'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pension Fund Information Card */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">
              Informations de la caisse
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la caisse
                </label>
                <div className="flex items-center px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                  <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-900">{pensionFund.fund_name || '-'}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    pensionFund.status === 'Terminé' ? 'bg-green-100 text-green-800' :
                    pensionFund.status === 'Transféré' ? 'bg-green-100 text-green-800' :
                    pensionFund.status === 'En cours' ? 'bg-blue-100 text-blue-800' :
                    pensionFund.status === 'Nouveau' ? 'bg-gray-100 text-gray-800' :
                    pensionFund.status === 'Rejeté' ? 'bg-red-100 text-red-800' :
                    pensionFund.status === 'Annulé' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {pensionFund.status || 'Non défini'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  À traiter
                </label>
                <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    pensionFund.to_do ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {pensionFund.to_do ? 'Oui' : 'Non'}
                  </span>
                </div>
              </div>
            </div>

            <h3 className="text-lg font-medium text-gray-900 mt-8 mb-4">Finances</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant
                </label>
                <div className="flex items-center px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                  <CreditCard className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-gray-900 font-medium">
                    {pensionFund.amount ? `CHF ${parseFloat(pensionFund.amount).toLocaleString('fr-CH')}` : '-'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commissionné
                </label>
                <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    pensionFund.commissioned === 'yes' || pensionFund.commissioned === 'Oui'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {pensionFund.commissioned === 'yes' || pensionFund.commissioned === 'Oui' ? 'Oui' : 'Non'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Montant commission
                </label>
                <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                  {pensionFund.commission_amount ? `CHF ${parseFloat(pensionFund.commission_amount).toLocaleString('fr-CH')}` : '-'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date décompte sortie
                </label>
                <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                  {pensionFund.exit_statement_date
                    ? new Date(pensionFund.exit_statement_date).toLocaleDateString('fr-CH')
                    : '-'}
                </div>
              </div>
            </div>

            <h3 className="text-lg font-medium text-gray-900 mt-8 mb-4">Dates</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date création
                </label>
                <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                  {pensionFund.created_at
                    ? new Date(pensionFund.created_at).toLocaleDateString('fr-CH')
                    : '-'}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dernière mise à jour
                </label>
                <div className="px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                  {pensionFund.updated_at
                    ? new Date(pensionFund.updated_at).toLocaleDateString('fr-CH')
                    : '-'}
                </div>
              </div>
            </div>

            {pensionFund.comments && (
              <>
                <h3 className="text-lg font-medium text-gray-900 mt-8 mb-4">Commentaires</h3>
                <div className="px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-900">
                  {pensionFund.comments}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'documents' && (
        <PensionFundDocuments
          pensionFund={pensionFund}
          getFileUrl={getFileUrl}
          handleFileUpload={handleFileUpload}
          handleFileDelete={handleFileDelete}
          deleteDocumentMutation={{ isPending: deleteDocumentMutation.isPending }}
          uploadDecompteMutation={{ isPending: uploadDecompteMutation.isPending }}
          uploadDemandeTransfertMutation={{ isPending: uploadDemandeTransfertMutation.isPending }}
          uploadInstructionMutation={{ isPending: uploadInstructionMutation.isPending }}
          signInstructionMutation={{
            isPending: signInstructionMutation.isPending,
            mutate: (fondationName: string) => signInstructionMutation.mutate(fondationName),
          }}
        />
      )}

      {activeTab === 'actions' && (
        <PensionFundStatusForm
          pensionFund={pensionFund}
          statusColors={statusColors}
          selectedStatus={selectedStatus}
          showInstructionFields={showInstructionFields}
          showAmountField={showAmountField}
          mergeDocsSelected={mergeDocsSelected}
          isMerging={isMerging}
          instructionLemaniaRef={instructionLemaniaRef}
          instructionBanqueRef={instructionBanqueRef}
          decompteRef={decompteRef}
          getAvailableStatuses={getAvailableStatuses}
          handleStatusChange={handleStatusChange}
          handleConfirmStatus={handleConfirmStatus}
          handleMergeDocuments={handleMergeDocuments}
          setMergeDocsSelected={setMergeDocsSelected}
          updateStatusMutation={{ isPending: updateStatusMutation.isPending }}
          validateMutation={{
            isPending: validateMutation.isPending,
            mutate: () => validateMutation.mutate(),
          }}
          uebertragResult={uebertragResult}
          uebertragIsSubmitting={uebertragIsSubmitting}
          handleUebertragSubmit={handleUebertragSubmit}
          handleStatusChange2={handleStatusChange}
          getFileUrl={getFileUrl}
          onRefetch={() => queryClient.invalidateQueries({ queryKey: ['pension-fund', uuid] })}
        />
      )}

      {activeTab === 'activite' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">Historique</h2>
          {pensionFund.activities && pensionFund.activities.length > 0 ? (
            <div className="space-y-4">
              {pensionFund.activities.map((activity) => {
                const getActivityIcon = (type: string) => {
                  switch (type) {
                    case 'pension_fund_status_changed': return '🔄';
                    case 'pension_fund_validated': return '✅';
                    case 'pension_fund_invalidated': return '❌';
                    case 'pension_fund_document_uploaded': return '📤';
                    case 'pension_fund_document_deleted': return '🗑️';
                    case 'pension_fund_amount_changed': return '💰';
                    case 'pension_fund_commission_changed': return '📊';
                    default: return '📋';
                  }
                };

                const getActivityTitle = (type: string) => {
                  switch (type) {
                    case 'pension_fund_status_changed': return 'Changement de statut';
                    case 'pension_fund_validated': return 'Transfert validé';
                    case 'pension_fund_invalidated': return 'Transfert invalidé';
                    case 'pension_fund_document_uploaded': return 'Document uploadé';
                    case 'pension_fund_document_deleted': return 'Document supprimé';
                    case 'pension_fund_amount_changed': return 'Montant modifié';
                    case 'pension_fund_commission_changed': return 'Commission modifiée';
                    default: return 'Activité';
                  }
                };

                return (
                  <div
                    key={activity.id}
                    className="border-2 rounded-lg p-4 bg-purple-50 border-purple-200 transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{getActivityIcon(activity.activity_type)}</span>
                          <h3 className="font-medium text-gray-900">{getActivityTitle(activity.activity_type)}</h3>
                        </div>

                        <div className="mt-2 text-sm text-gray-600 space-y-1">
                          {activity.description && (
                            <div className="text-gray-700">{activity.description}</div>
                          )}

                          {activity.old_value && activity.new_value && (
                            <div>
                              <span className="text-red-600">{activity.old_value}</span>
                              {' → '}
                              <span className="text-green-600">{activity.new_value}</span>
                            </div>
                          )}
                        </div>

                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(activity.created_at).toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                          {activity.user && (
                            <span className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {activity.user.name || `Agent #${activity.user_id}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-gray-500">Aucune activité enregistrée</p>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default PensionFundDetails;
